// frontend/src/pages/ApiPrice.js
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// 訂閱定價頁面：讓用戶選擇方案與計費週期，點選訂閱後轉跳到付款頁面
const ApiPrice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 方案類型狀態（預設為個人方案）
  const [planType, setPlanType] = useState("individual");
  // 計費週期狀態（預設為每月，monthly）
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // 方案價格設定（金額單位：每月基本價格）
  const pricing = {
    "free": 0,               // 免費版
    "basic-label": 300,      // 基礎版 - 標籤
    "basic-risk": 600,       // 基礎版 - 風險評估
    "advanced": 3000         // 進階版 - 標籤+風險評估
  };

  // 計算最終價格，依據計費週期進行不同倍數與折扣計算
  const calculatePrice = (basePrice) => {
    switch (billingPeriod) {
      case "annually": // 年付方案：12 個月，8折優惠
        return Math.round((basePrice * 12) * 0.8);
      default: // 每月方案，直接顯示基本價格
        return basePrice;
    }
  };

  // 依據計費週期取得價格顯示單位文字
  const getPeriodLabel = () => {
    switch (billingPeriod) {
      case "annually":
        return t("ApiPrice.periodAnnual", "for 12 months");
      default:
        return t("ApiPrice.month", "per month");
    }
  };

  // 點擊訂閱按鈕時，計算最終金額並轉跳至付款頁面
  const handleSubscribe = (plan) => {
    // 依據選定方案計算最終金額（四捨五入到整數）
    const finalPrice = calculatePrice(pricing[plan]);
    // 利用 URLSearchParams 組成查詢字串，方便付款頁面讀取資訊
    const queryParams = new URLSearchParams({
      plan,        // 方案名稱
      planType,    // 方案類型
      billingPeriod, // 計費週期
      amount: finalPrice // 計算後的金額
    });
    // 轉跳至付款頁面
    navigate(`/payment?${queryParams.toString()}`);
  };

  // 新增聯絡我們按鈕的點擊處理函數
  const handleContactUs = () => {
    navigate('/contact');
  };

  // 用於渲染單一方案卡片，方便維護
  const renderPlanCard = (planKey, planTitle, additionalFeatures = []) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 flex flex-col">
      <h2 className="text-2xl font-bold mb-6">
        {t(`ApiPrice.${planKey}`, planTitle)}
      </h2>
      <div className="mb-6">
        {/* 使用 calculatePrice 計算動態價格 */}
        <span className="text-4xl font-bold">NT${calculatePrice(pricing[planKey])}</span>
        <span className="text-gray-500 dark:text-gray-400"> {getPeriodLabel()}</span>
      </div>
      <button
        className={`w-full ${planKey === "free" ? "border border-gray-300 text-gray-300" : "border border-blue-500 text-blue-500"} rounded-full py-3 mb-6`}
        onClick={() => handleSubscribe(planKey)}
      >
        {planKey === "free" ? t("ApiPrice.startFree", "Start Free") : t("ApiPrice.subscribe", "Subscribe")}
      </button>
      <ul className="space-y-2">
        {additionalFeatures.map((feature, idx) => (
          <li key={idx} className="flex items-start">
            <span className="mr-2">•</span>
            <span>{t(`ApiPrice.feature${planKey}${idx}`, feature)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-[#f5f3f3] dark:bg-gray-900">
      {/* Header 區塊 */}
      <header className="w-full bg-white dark:bg-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start">
          <h1 className="text-5xl font-bold text-black dark:text-white mb-6 md:mb-0">
            {t("ApiPrice.apis", "APIs")}
          </h1>
          <p className="max-w-md text-black dark:text-white">
            {t("ApiPrice.description", "Enhance your product's compliance integration with our industry-leading API (Application Programming Interface, API) for compliance score and address labels.")}
          </p>
        </div>
      </header>

      {/* 定價卡片區塊 */}
      <main className="flex-grow py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* 方案類型選擇 */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-md shadow-sm bg-white dark:bg-gray-800 p-1">
              <button
                onClick={() => setPlanType("individual")}
                className={`px-8 py-3 text-sm font-medium rounded-md ${planType === "individual" ? "bg-gradient-to-r from-blue-400 to-purple-500 text-white" : "text-gray-700 dark:text-gray-300"}`}
              >
                {t("ApiPrice.individual", "Individual")}
              </button>
              <button
                onClick={() => setPlanType("team")}
                className={`px-8 py-3 text-sm font-medium rounded-md ${planType === "team" ? "bg-gradient-to-r from-blue-400 to-purple-500 text-white" : "text-gray-700 dark:text-gray-300"}`}
              >
                {t("ApiPrice.team", "Team")}
              </button>
            </div>
          </div>

          {/* 計費週期選擇 */}
          <div className="flex justify-center mb-10">
            <div className="grid grid-cols-2 w-full max-w-md">
              {["monthly", "annually"].map((period) => (
                <button
                  key={period}
                  onClick={() => setBillingPeriod(period)}
                  className={`py-2 text-center ${billingPeriod === period ? "border-b-2 border-blue-500 text-blue-600 font-medium" : "text-gray-500"}`}
                >
                  {t(`ApiPrice.${period}`, period.charAt(0).toUpperCase() + period.slice(1))}
                </button>
              ))}
            </div>
          </div>

          {/* 定價卡片群組 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* 免費版 */}
            {renderPlanCard("free", "Free", [
              "包含錢包標籤和風險評估",
              "配額：每日10次請求",
              "支援13條主要區塊鏈",
              "基本查詢功能"
            ])}
            
            {/* 基礎版 - 標籤 */}
            {renderPlanCard("basic-label", "Basic - Label", [
              "錢包標籤查詢",
              "配額：每日1,000次請求",
              "限制：每秒5次請求",
              "支援所有主要區塊鏈"
            ])}
            
            {/* 基礎版 - 風險評估 */}
            {renderPlanCard("basic-risk", "Basic - Risk Assessment", [
              "錢包風險評估查詢",
              "配額：每日1,000次請求",
              "限制：每秒5次請求", 
              "支援所有主要區塊鏈"
            ])}
            
            {/* 進階版 - 標籤+風險評估 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-blue-500 p-8 flex flex-col relative">
              <div className="absolute -top-1 right-4">
                <div className="bg-red-500 text-white px-3 py-1 font-bold rounded-b-md">
                  {t("ApiPrice.best", "BEST")}
                </div>
              </div>
              <h2 className="text-2xl font-bold text-blue-500 mb-6">
                {t("ApiPrice.advanced", "Advanced - Label & Risk")}
              </h2>
              <div className="mb-6">
                <span className="text-4xl font-bold">NT${calculatePrice(pricing["advanced"])}</span>
                <span className="text-gray-500 dark:text-gray-400"> {getPeriodLabel()}</span>
              </div>
              <button className="w-full bg-blue-500 text-white rounded-full py-3 mb-6" onClick={() => handleSubscribe("advanced")}>
                {t("ApiPrice.subscribe", "Subscribe")}
              </button>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t("ApiPrice.labelRiskAPI", "錢包標籤查詢")}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t("ApiPrice.riskAssessment", "錢包風險評估")}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {t("ApiPrice.advancedQuotaPrefix", "配額：")}
                    <span className="text-red-500">10,000</span>
                    {t("ApiPrice.advancedQuotaSuffix", "次請求")}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t("ApiPrice.advancedLimit", "限制：每秒5次請求")}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t("ApiPrice.prioritySupport", "優先技術支援")}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 加值服務區塊 */}
          <div className="grid grid-cols-1 gap-6">
            {/* 聯絡我們區塊 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 flex flex-col items-center text-center">
              <h2 className="text-2xl font-bold mb-4">
                {t("ApiPrice.needMoreAPICalls", "需要更多 API 調用或技術支援？")}
              </h2>
              <p className="text-lg mb-6 max-w-2xl">
                {t("ApiPrice.contactDescription", "無論是想要增加 API 調用配額、需要專業的技術支援，或有任何其他需求，我們都樂意為您提供協助。歡迎隨時與我們聯繫，我們將為您提供最適合的解決方案。")}
              </p>
              <button 
                className="bg-black text-white rounded-full py-3 px-8 w-full md:w-auto hover:bg-gray-800 transition-colors duration-300"
                onClick={handleContactUs}
              >
                {t("ApiPrice.contactUs", "聯絡我們")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApiPrice;
